import { Field, Form, Formik } from 'formik';
import ms from 'ms';
import type { Dispatch, SetStateAction } from 'react';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconProps } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from 'styled-components';

import Icon from '@tmh-website/components/icons/phosphor-extension/Icon';
import CMSLink from '@tmh-website/components/shared/CMSLink';
import CollapseContainer from '@tmh-website/components/shared/CollapseContainer';
import HeadlineTextBlock from '@tmh-website/components/shared/HeadlineTextBlock';
import HorizontalScrollContainer from '@tmh-website/components/shared/HorizontalScrollContainer';
import Richtext from '@tmh-website/components/shared/Richtext';
import { getContactSupport, getIconSvgObjectById } from '@tmh-website/services/cms.client.service';
import { GlobalContext, useMediaQuery } from '@tmh-website/shared';
import { ContactSupport, IconSvgObject, isValidLinkField } from '@tmh-website/shared/cms';
import { Audience, Breakpoint, type LocalePrefix } from '@tmh-website/shared/const';
import StyledTypo from '@tmh-website/styles/typo';
import Button from '@tmh-website/ui/Button';
import Select from '@tmh-website/ui/Select';

import ContactCard from './ContactCard';
import * as Styled from './ContactSupport.styles';
import { ContactSupportThemeColor, contactSupportThemes } from './ContactSupport.theme';

/**
 * Only keep entries with the correct audiences and groups with entries
 */
export function filterEntriesByAudience(
  entries?: ContactSupport['contactSupportCategories'],
  audience?: Audience | null
) {
  if (!audience || !entries) {
    return [];
  }
  const visibleCategories = (entries ?? []).filter(category =>
    category.contactSupportFaqEntries?.some(entry => entry.audiences.includes(audience))
  );

  return visibleCategories.map(category => {
    const visibleEntries = (category.contactSupportFaqEntries ?? []).filter(entry =>
      entry.audiences.includes(audience)
    );
    return { ...category, contactSupportFaqEntries: visibleEntries.length > 0 ? visibleEntries : undefined };
  });
}

/**
 * ContactSupportEntryIcon
 * An icon component that automatically fetches the icon from the server if needed.
 */

interface ContactSupportEntryIconProps {
  icon?: IconSvgObject | string;
  iconProps?: IconProps;
}

const ContactSupportEntryIcon = ({ icon, iconProps }: ContactSupportEntryIconProps) => {
  const hasToBeFetched = typeof icon === 'string';
  const { data } = useQuery(
    ['icon', hasToBeFetched ? icon : 'disabled'],
    () => getIconSvgObjectById(icon as string, iconProps),
    {
      staleTime: ms('24h'),
      keepPreviousData: true,
      enabled: hasToBeFetched,
    }
  );

  const iconSvgObject = hasToBeFetched ? data?.data : icon;

  if (!iconSvgObject) {
    return <svg width={40} height={40} />;
  }

  return <Icon size={40} icon={iconSvgObject} />;
};

/**
 * ContactSupportEntry
 * An entry in the faq section of the contact support block.
 */

interface ContactSupportEntryProps {
  themeColor: ContactSupportThemeColor;
  headline: string;
  text: string;
}

const ContactSupportEntry = ({ themeColor, headline, text, ...props }: ContactSupportEntryProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { textOpacity } = useTheme();

  return (
    <Styled.Entry onClick={() => setIsOpen(!isOpen)} $themeColor={themeColor} $isOpen={isOpen} {...props}>
      <Styled.EntryHeadlineContainer>
        <StyledTypo.Typography $style='uiTextBold'>{headline}</StyledTypo.Typography>
        <Styled.AdjustedCaretDown size={16} weight='bold' $themeColor={themeColor} />
      </Styled.EntryHeadlineContainer>
      <CollapseContainer isOpen={isOpen}>
        <Styled.EntryText
          typographyProps={{
            $style: 'uiText',
            $color: contactSupportThemes[themeColor].text,
            $opacity: textOpacity,
          }}
          input={text}
        />
      </CollapseContainer>
    </Styled.Entry>
  );
};

/**
 * ValuesWatcher
 * Handles state changes on value change
 */

type ValuesMatcherProps = {
  audience: Audience | null;
  setSelectedAudience: Dispatch<SetStateAction<Audience | null>>;
};

const ValuesWatcher = ({ audience, setSelectedAudience }: ValuesMatcherProps) => {
  useEffect(() => {
    if (!audience) {
      return;
    }

    setSelectedAudience(audience);
  }, [audience]);

  return null;
};

/**
 * ContactSupportBase
 * The base of the contact support block.
 */

interface ContactSupportSectionProps {
  /**
   * This component is used in a site wide popup and doesn't have the initial data available there.
   * Set this to undefined to load all needed data client side on render.
   */
  contactSupportData?: ContactSupport;
  themeColor: ContactSupportThemeColor;
  locale: LocalePrefix;
  currentPath: string;
}

const ContactSupportBase = ({ contactSupportData, themeColor, locale, currentPath }: ContactSupportSectionProps) => {
  const isClientSide = !contactSupportData;
  const [activeCategory, setActiveCategory] = useState(contactSupportData?.contactSupportCategories?.[0].id ?? null);
  const { audience } = useContext(GlobalContext);
  const [selectedAudience, setSelectedAudience] = useState(audience);
  const isBreakpointLgUp = useMediaQuery(`(min-width: ${Breakpoint.Lg}px)`);
  const ScrollContainer = isBreakpointLgUp ? Fragment : HorizontalScrollContainer;
  const { t } = useTranslation();
  const { textOpacity } = useTheme();

  const { data } = useQuery(['contact-support', locale], () => getContactSupport(locale), {
    staleTime: ms('10min'),
    keepPreviousData: true,
    enabled: isClientSide,
    initialData: isClientSide ? undefined : contactSupportData,
  });
  const filteredCategories = filterEntriesByAudience(data?.contactSupportCategories, selectedAudience);
  const currentCategory = filteredCategories?.find(category => category.id === activeCategory);

  useEffect(() => {
    if (!currentCategory) {
      setActiveCategory(filteredCategories?.[0]?.id ?? null);
    }
  }, [filteredCategories]);

  if (!data) {
    return null;
  }
  // The icon is either a string (client) or an object (server)
  const typedIcon = currentCategory?.icon as IconSvgObject | string;
  return (
    <>
      <Styled.FromContainer>
        <Formik initialValues={{ audience: selectedAudience }} onSubmit={() => {}}>
          {({ values }) => (
            <Form>
              <ValuesWatcher audience={values.audience} setSelectedAudience={setSelectedAudience} />
              <Styled.AudienceSelectContainer>
                <Field
                  as={Select}
                  name='audience'
                  label={t('global:audience.label')}
                  options={Object.values(Audience)
                    .filter(audience => audience !== Audience.Default)
                    .map(audience => ({
                      label: t(`global:audience.${audience}`),
                      value: audience,
                    }))}
                />
              </Styled.AudienceSelectContainer>
            </Form>
          )}
        </Formik>
      </Styled.FromContainer>
      {(data.headline || data.text) && (
        <HeadlineTextBlock
          headline={data.headline}
          text={data.text}
          textColor={contactSupportThemes[themeColor].text}
          textAlign='center'
          headlineStyle='h5'
          hasMarginAfterHeadline
        />
      )}
      <Styled.Container>
        <Styled.Sidebar>
          <ScrollContainer>
            {filteredCategories?.map(category => (
              <Styled.SidebarButton
                key={category.id}
                $themeColor={contactSupportThemes[themeColor].sidebarButtonThemeColor}
                onClick={() => setActiveCategory(category.id)}
                $isActive={activeCategory === category.id}>
                {category.headline}
              </Styled.SidebarButton>
            ))}
          </ScrollContainer>
        </Styled.Sidebar>
        {currentCategory && (
          <Styled.Content>
            <Styled.HeadlineContainer>
              <ContactSupportEntryIcon icon={typedIcon} />
              <div>
                <Styled.Headline $style='h5' as='p'>
                  {currentCategory.headline}
                </Styled.Headline>
                {currentCategory.text && (
                  <Richtext
                    input={currentCategory.text}
                    typographyProps={{
                      $style: 'uiText',
                      $color: contactSupportThemes[themeColor].text,
                      $opacity: textOpacity,
                      as: 'p',
                    }}
                  />
                )}
              </div>
            </Styled.HeadlineContainer>
            <ContactCard
              data={currentCategory as ContactSupport['contactSupportCategories'][number]}
              themeColor={contactSupportThemes[themeColor].contactCardThemeColor}
              locale={locale}
              currentPath={currentPath}>
              {isBreakpointLgUp &&
                currentCategory?.contactSupportFaqEntries?.map(entry => (
                  <ContactSupportEntry
                    key={`${currentCategory.id}_${entry.id}`}
                    themeColor={themeColor}
                    headline={entry.headline}
                    text={entry.text}
                  />
                ))}
            </ContactCard>
            {!isBreakpointLgUp &&
              currentCategory?.contactSupportFaqEntries?.map(entry => (
                <ContactSupportEntry
                  key={`${currentCategory.id}_${entry.id}`}
                  themeColor={themeColor}
                  headline={entry.headline}
                  text={entry.text}
                />
              ))}
            {isValidLinkField(currentCategory?.buttonLinkField) && (
              <CMSLink link={currentCategory?.buttonLinkField}>
                <Button
                  isSmall
                  themeColor={contactSupportThemes[themeColor].buttonThemeColor}
                  hoverThemeColor={contactSupportThemes[themeColor].buttonHoverThemeColor}>
                  {currentCategory?.buttonLinkField.label}
                </Button>
              </CMSLink>
            )}
          </Styled.Content>
        )}
      </Styled.Container>
    </>
  );
};

export default ContactSupportBase;
